class CartDrawer extends HTMLElement {
	constructor() {
		super();

		this.addEventListener(
			"keyup",
			(evt) => evt.code === "Escape" && this.close()
		);
		this.querySelector("#CartDrawer-Overlay").addEventListener(
			"click",
			this.close.bind(this)
		);
		this.setHeaderCartIconAccessibility();
	}

	setHeaderCartIconAccessibility() {
		const cartLink = document.querySelectorAll(".cart-open-trigger");
		cartLink.forEach((links) => {
			links.setAttribute("role", "button");
			links.setAttribute("aria-haspopup", "dialog");
			links.addEventListener("click", (event) => {
				event.preventDefault();
				this.open(links);
			});
			links.addEventListener("keydown", (event) => {
				if (event.code.toUpperCase() === "SPACE") {
					event.preventDefault();
					this.open(links);
				}
			});
		});
	}

	open(triggeredBy) {
		if (triggeredBy) this.setActiveElement(triggeredBy);
		this.clearInitialFocusTimers();
		const containerToTrapFocusOn = document.getElementById("CartDrawer");
		const focusElement = this.getInitialFocusElement(containerToTrapFocusOn);
		const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
		if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
			this.setSummaryAccessibility(cartDrawerNote);
		this.suspendFooterTotalsDuringOpen();

		const drawerInner = this.querySelector(".drawer__inner");
		const focusDrawer = () => {
			if (!containerToTrapFocusOn || !focusElement) return;
			trapFocus(containerToTrapFocusOn, focusElement);
			this.focusInitialElement(focusElement);
		};

		// here the animation doesn't seem to always get triggered. A timeout seem to help
		setTimeout(() => {
			this.classList.add("animate", "active");
			focusDrawer();
		});

		[100, 350, 650].forEach((delay) => {
			this.initialFocusTimers.push(setTimeout(focusDrawer, delay));
		});
		this.initialFocusTimers.push(
			setTimeout(() => this.restoreFooterTotalsAfterOpen(), 700)
		);

		if (drawerInner) {
			const handleTransitionEnd = (event) => {
				if (event.target !== drawerInner) return;
				drawerInner.removeEventListener("transitionend", handleTransitionEnd);
				focusDrawer();
			};

			drawerInner.addEventListener("transitionend", handleTransitionEnd);
		}

		this.setOutsideInert(true);
		document.body.classList.add("overflow-hidden");
	}

	getInitialFocusElement(container) {
		return (
			this.querySelector(".drawer__close") ||
			(container && typeof getFocusableElements === "function"
				? getFocusableElements(container)[0]
				: null)
		);
	}

	focusInitialElement(element) {
		try {
			element.focus({ preventScroll: true });
		} catch (error) {
			element.focus();
		}
	}

	clearInitialFocusTimers() {
		if (!this.initialFocusTimers) {
			this.initialFocusTimers = [];
			return;
		}
		this.initialFocusTimers.forEach((timer) => clearTimeout(timer));
		this.initialFocusTimers = [];
	}

	suspendFooterTotalsDuringOpen() {
		this.restoreFooterTotalsAfterOpen();
		this.querySelectorAll(".cart-drawer__footer .totals").forEach((total) => {
			if (total.hasAttribute("aria-hidden")) return;
			total.setAttribute("data-cart-drawer-opening-hidden", "");
			total.setAttribute("aria-hidden", "true");
		});
	}

	restoreFooterTotalsAfterOpen() {
		this.querySelectorAll("[data-cart-drawer-opening-hidden]").forEach((total) => {
			total.removeAttribute("aria-hidden");
			total.removeAttribute("data-cart-drawer-opening-hidden");
		});
	}

	close() {
		this.clearInitialFocusTimers();
		this.restoreFooterTotalsAfterOpen();
		this.classList.remove("active");
		this.setOutsideInert(false);
		removeTrapFocus(this.activeElement);
		document.body.classList.remove("overflow-hidden");
	}

	setOutsideInert(on) {
		Array.from(document.body.children).forEach((child) => {
			if (child === this) return;
			if (child.hasAttribute("data-cart-drawer-announcer")) return;
			if (on) {
				child.setAttribute("data-cart-drawer-inert", "");
				child.setAttribute("inert", "");
			} else if (child.hasAttribute("data-cart-drawer-inert")) {
				child.removeAttribute("data-cart-drawer-inert");
				child.removeAttribute("inert");
			}
		});
	}

	setSummaryAccessibility(cartDrawerNote) {
		cartDrawerNote.setAttribute("role", "button");
		cartDrawerNote.setAttribute("aria-expanded", "false");

		if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
			cartDrawerNote.setAttribute(
				"aria-controls",
				cartDrawerNote.nextElementSibling.id
			);
		}

		cartDrawerNote.addEventListener("click", (event) => {
			event.currentTarget.setAttribute(
				"aria-expanded",
				!event.currentTarget.closest("details").hasAttribute("open")
			);
		});

		cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
	}

	renderContents(parsedState) {
		this.querySelector(".drawer__inner").classList.contains("is-empty") &&
			this.querySelector(".drawer__inner").classList.remove("is-empty");
		this.productId = parsedState.id;
		this.getSectionsToRender().forEach((section) => {
			const sectionElement = section.selector
				? document.querySelector(section.selector)
				: document.getElementById(section.id);
			sectionElement.innerHTML = this.getSectionInnerHTML(
				parsedState.sections[section.id],
				section.selector
			);
		});

		setTimeout(() => {
			this.querySelector("#CartDrawer-Overlay").addEventListener(
				"click",
				this.close.bind(this)
			);
			this.open();
		});
	}

	getSectionInnerHTML(html, selector = ".shopify-section") {
		return new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector).innerHTML;
	}

	getSectionsToRender() {
		return [
			{
				id: "cart-drawer",
				selector: "#CartDrawer",
			},
			{
				id: "cart-icon-bubble",
			},
			{
				id: "cart-icon-bubble-mobile",
			},
		];
	}

	getSectionDOM(html, selector = ".shopify-section") {
		return new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector);
	}

	setActiveElement(element) {
		this.activeElement = element;
	}
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
	getSectionsToRender() {
		return [
			{
				id: "CartDrawer",
				section: "cart-drawer",
				selector: ".drawer__inner",
			},
			{
				id: "cart-icon-bubble",
				section: "cart-icon-bubble",
				selector: ".shopify-section",
			},
			{
				id: "cart-icon-bubble-mobile",
				section: "cart-icon-bubble-mobile",
				selector: ".shopify-section",
			},
		];
	}

	enableLoading(line) {
		const wrap = document.getElementById("CartDrawer-CartItems");
		if (wrap) wrap.classList.add("cart__items--disabled");
		this.querySelectorAll(
			`#CartDrawer-Item-${line} .loading-overlay`
		).forEach((o) => o.classList.remove("hidden"));
		if (this.lineItemStatusElement) {
			this.lineItemStatusElement.setAttribute("aria-hidden", false);
		}
	}

	updateQuantity(line, quantity, name) {
		this.enableLoading(line);
		this.querySelectorAll(".quantity__button").forEach((b) =>
			b.classList.add("disabled")
		);

		let removedProductName = "";
		if (quantity === 0 || quantity === "0") {
			const titleEl = document.querySelector(
				`#CartDrawer-Item-${line} .cart-item__name`
			);
			if (titleEl) removedProductName = titleEl.textContent.trim();
		}

		const body = JSON.stringify({
			line,
			quantity,
			sections: this.getSectionsToRender().map((s) => s.section),
			sections_url: window.location.pathname,
		});

		fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
			.then((response) => response.text())
			.then((state) => {
				const parsedState = JSON.parse(state);

				if (parsedState.errors) {
					const qEl = document.getElementById(`Quantity-${line}`);
					if (qEl) qEl.value = qEl.getAttribute("value");
					this.updateLiveRegions(line, parsedState.errors);
					return;
				}

				const prevItemCount = this.querySelectorAll(".cart-item").length;
				const structural =
					parsedState.item_count === 0 ||
					parsedState.items.length !== prevItemCount;

				if (structural) {
					this.applyFullReplace(parsedState, line, name, removedProductName);
					if (removedProductName) {
						this.announceRemoval(removedProductName);
					}
				} else {
					this.applyInPlaceUpdate(parsedState, line);
				}

				publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-items" });
			})
			.catch(() => {
				this.querySelectorAll(".loading-overlay").forEach((o) =>
					o.classList.add("hidden")
				);
				this.querySelectorAll(".quantity__button").forEach((b) =>
					b.classList.remove("disabled")
				);
				const errors = document.getElementById("CartDrawer-CartErrors");
				if (errors) errors.textContent = window.cartStrings.error;
			})
			.finally(() => {
				this.querySelectorAll(".quantity__button").forEach((b) =>
					b.classList.remove("disabled")
				);
				this.disableLoading(line);
			});
	}

	announceRemoval(productName) {
		const announcer = document.getElementById(
			"cart-drawer-quantity-announcer"
		);
		if (!announcer) return;
		clearTimeout(this._removalAnnounceTimeout);
		announcer.textContent = "";
		this._removalAnnounceTimeout = setTimeout(() => {
			announcer.textContent = `${productName} removed from cart`;
		}, 150);
	}

	applyInPlaceUpdate(parsedState, changedLine) {
		const newDoc = new DOMParser().parseFromString(
			parsedState.sections["cart-drawer"],
			"text/html"
		);

		parsedState.items.forEach((item, idx) => {
			const lineNum = idx + 1;

			const input = document.getElementById(`Quantity-${lineNum}`);
			if (input && String(input.value) !== String(item.quantity)) {
				input.value = item.quantity;
				input.setAttribute("value", item.quantity);
			}

			const newLine = newDoc.getElementById(`CartDrawer-Item-${lineNum}`);
			const oldLine = document.getElementById(`CartDrawer-Item-${lineNum}`);
			if (!newLine || !oldLine) return;

			const newPrice = newLine.querySelector(
				".cart-item__quantity-price > :first-child"
			);
			const oldPrice = oldLine.querySelector(
				".cart-item__quantity-price > :first-child"
			);
			if (
				newPrice &&
				oldPrice &&
				!oldPrice.contains(document.activeElement)
			) {
				oldPrice.replaceWith(newPrice.cloneNode(true));
			}

			// Cap City: también refresca las etiquetas de descuento de la línea (BOGO)
			const newLineDiscounts = newLine.querySelector(".discounts");
			const oldLineDiscounts = oldLine.querySelector(".discounts");
			if (newLineDiscounts && oldLineDiscounts) {
				oldLineDiscounts.innerHTML = newLineDiscounts.innerHTML;
			}
		});

		const newSubtotal = newDoc.querySelector(
			".drawer__footer .totals__subtotal-value"
		);
		const oldSubtotal = document.querySelector(
			".drawer__footer .totals__subtotal-value"
		);
		if (newSubtotal && oldSubtotal) {
			oldSubtotal.textContent = newSubtotal.textContent;
		}

		const newDiscounts = newDoc.querySelector(".drawer__footer .discounts");
		const oldDiscounts = document.querySelector(".drawer__footer .discounts");
		if (newDiscounts && oldDiscounts) {
			oldDiscounts.innerHTML = newDiscounts.innerHTML;
		}

		// Cap City: refresca ahorro, cuotas de Shop Pay y upsell BOGO
		// (todo elemento del drawer marcado con data-cart-refresh y un id)
		newDoc.querySelectorAll("[data-cart-refresh][id]").forEach((newEl) => {
			const oldEl = document.getElementById(newEl.id);
			if (!oldEl || oldEl.contains(document.activeElement)) return;
			oldEl.replaceWith(document.importNode(newEl, true));
		});

		[
			{ id: "cart-icon-bubble", section: "cart-icon-bubble" },
			{ id: "cart-icon-bubble-mobile", section: "cart-icon-bubble-mobile" },
		].forEach((s) => {
			const el = document.getElementById(s.id);
			if (!el || !parsedState.sections[s.section]) return;
			el.innerHTML = this.getSectionInnerHTML(
				parsedState.sections[s.section],
				".shopify-section"
			);
		});
	}

	applyFullReplace(parsedState, line, name, removedProductName) {
		const cartDrawerWrapper = document.querySelector("cart-drawer");
		this.classList.toggle("is-empty", parsedState.item_count === 0);
		if (cartDrawerWrapper) {
			cartDrawerWrapper.classList.toggle(
				"is-empty",
				parsedState.item_count === 0
			);
		}

		this.getSectionsToRender().forEach((section) => {
			const host = document.getElementById(section.id);
			if (!host) return;
			const el = host.querySelector(section.selector) || host;
			el.innerHTML = this.getSectionInnerHTML(
				parsedState.sections[section.section],
				section.selector
			);
		});

		const lineItem = document.getElementById(`CartDrawer-Item-${line}`);
		if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
			trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`));
		} else if (parsedState.item_count === 0 && cartDrawerWrapper) {
			trapFocus(
				cartDrawerWrapper,
				cartDrawerWrapper.querySelector(".drawer__inner-empty a")
			);
		} else if (removedProductName && cartDrawerWrapper) {
			const heading = cartDrawerWrapper.querySelector(".drawer__heading");
			if (heading) trapFocus(cartDrawerWrapper, heading);
		} else if (document.querySelector(".cart-item") && cartDrawerWrapper) {
			trapFocus(cartDrawerWrapper, document.querySelector(".cart-item__name"));
		}
	}
}

customElements.define("cart-drawer-items", CartDrawerItems);